/**
 * Seat-holder Diagnostics health in Cloud product language.
 *
 * Ready is never inferred from a persisted running value. Missing or stale
 * snapshot data stays Unknown.
 */

import type { CmmdRuntimeDiagnosticsSnapshot } from "@cmmd-forge/contracts";

import type { ForgeRuntimeStatus } from "../../cmmd/forgeRuntimeStatus";
import type { CloudEnvironmentLifecycleProjection } from "../../environments/primary/cloudEnvironmentLifecycleProjection";

export type DiagnosticsSeatHealthState = "sleeping" | "ready" | "paused" | "recover" | "unknown";

export interface DiagnosticsSeatHealth {
  readonly state: DiagnosticsSeatHealthState;
  readonly label: string;
  readonly detail: string;
  readonly failedStage: string | null;
}

const SNAPSHOT_FAILURE_STAGES: Readonly<
  Record<CmmdRuntimeDiagnosticsSnapshot["failure"]["classification"], string | null>
> = {
  none: null,
  runtime_error: "Environment error",
  authority_mismatch: "Provider authority",
  identity_mismatch: "Identity",
  revoked: "Access revoked",
  missing: "Missing observation",
  unknown: "Unknown failure",
};

function snapshotFailedStage(snapshot: CmmdRuntimeDiagnosticsSnapshot | undefined): string | null {
  if (!snapshot) return null;
  const classified = SNAPSHOT_FAILURE_STAGES[snapshot.failure.classification];
  if (classified !== null) return classified;
  if (snapshot.service.state === "error") return "Supervised service";
  if (snapshot.forge.state === "stale") return "Forge build";
  if (snapshot.replication.state === "missing") return "Replication";
  return null;
}

const LIFECYCLE_HEALTH: Readonly<
  Record<
    CloudEnvironmentLifecycleProjection["state"],
    (
      lifecycle: CloudEnvironmentLifecycleProjection,
      failedStage: string | null,
    ) => DiagnosticsSeatHealth
  >
> = {
  paused: (lifecycle) => ({
    state: "paused",
    label: "Paused",
    detail: lifecycle.detail ?? "Resume before live work can wake this Environment.",
    failedStage: null,
  }),
  sleeping: (lifecycle) => ({
    state: "sleeping",
    label: "Sleeping",
    detail: lifecycle.detail ?? "Live work wakes this Environment automatically.",
    failedStage: null,
  }),
  ready: (lifecycle) => ({
    state: "ready",
    label: "Ready",
    detail: lifecycle.detail ?? "This Environment can accept live work.",
    failedStage: null,
  }),
  "needs-attention": recoverFromLifecycle,
  degraded: recoverFromLifecycle,
  unavailable: recoverFromLifecycle,
  provisioning: recoverFromLifecycle,
  starting: recoverFromLifecycle,
  deleting: recoverFromLifecycle,
  unknown: (lifecycle) => ({
    state: "unknown",
    label: "Unknown",
    detail: lifecycle.detail ?? "Current Environment health has not been observed.",
    failedStage: null,
  }),
};

function recoverFromLifecycle(
  lifecycle: CloudEnvironmentLifecycleProjection,
  failedStage: string | null,
): DiagnosticsSeatHealth {
  return {
    state: "recover",
    label: "Recover",
    detail: failedStage
      ? `${failedStage} needs attention.`
      : (lifecycle.detail ?? "This Environment needs attention."),
    failedStage: failedStage ?? lifecycle.label,
  };
}

function fromLifecycle(
  lifecycle: CloudEnvironmentLifecycleProjection,
  failedStage: string | null,
): DiagnosticsSeatHealth {
  return LIFECYCLE_HEALTH[lifecycle.state](lifecycle, failedStage);
}

const UNOBSERVED_AVAILABILITY = new Set([
  "missing",
  "stale",
  "unknown",
  "revoked",
  "identity_mismatch",
  "offline",
]);

function fromSnapshot(snapshot: CmmdRuntimeDiagnosticsSnapshot | undefined): DiagnosticsSeatHealth {
  if (!snapshot) {
    return {
      state: "unknown",
      label: "Unknown",
      detail: "Current Environment health has not been observed.",
      failedStage: null,
    };
  }
  if (UNOBSERVED_AVAILABILITY.has(snapshot.availability.state)) {
    return {
      state: "unknown",
      label: "Unknown",
      detail: "The Diagnostics snapshot is missing, stale, or failed.",
      failedStage: snapshotFailedStage(snapshot),
    };
  }
  const failedStage = snapshotFailedStage(snapshot);
  if (failedStage !== null) {
    return {
      state: "recover",
      label: "Recover",
      detail: `${failedStage} needs attention.`,
      failedStage,
    };
  }
  if (snapshot.lifecycle.state === "suspended") {
    return {
      state: "sleeping",
      label: "Sleeping",
      detail: "Live work wakes this Environment automatically.",
      failedStage: null,
    };
  }
  return {
    state: "unknown",
    label: "Unknown",
    detail: "No current execution proof is available.",
    failedStage: null,
  };
}

const SLEEPING_FROM_CATALOG: CloudEnvironmentLifecycleProjection = {
  state: "sleeping",
  label: "Sleeping",
  detail: "Live work wakes this Environment automatically.",
  tone: "muted",
  automaticWakeAllowed: true,
  shellConnected: false,
  shellMayEstablishReady: false,
};

const RECOVER_FROM_CATALOG: CloudEnvironmentLifecycleProjection = {
  state: "needs-attention",
  label: "Needs attention",
  detail: "This Environment needs attention.",
  tone: "destructive",
  automaticWakeAllowed: false,
  shellConnected: false,
  shellMayEstablishReady: false,
};

/**
 * Catalog status is not execution proof. `running` never becomes Ready.
 */
export function lifecycleFromCatalogStatus(
  status: ForgeRuntimeStatus,
): CloudEnvironmentLifecycleProjection | null {
  if (status === "suspended") return SLEEPING_FROM_CATALOG;
  if (status === "error") return RECOVER_FROM_CATALOG;
  return null;
}

export function projectDiagnosticsSeatHealth(input: {
  readonly lifecycle: CloudEnvironmentLifecycleProjection | null;
  readonly snapshot: CmmdRuntimeDiagnosticsSnapshot | undefined;
}): DiagnosticsSeatHealth {
  const failedStage = snapshotFailedStage(input.snapshot);
  if (input.lifecycle !== null) {
    return fromLifecycle(input.lifecycle, failedStage);
  }
  return fromSnapshot(input.snapshot);
}

export function seatHolderCopyIsForbidden(text: string): boolean {
  return /runtimeId|sprites\.dev|\bps\b/i.test(text);
}
